import { api_fetch, api_fetch_throws } from "./client"

// Types

export interface ContractDocument {
  id: string
  org_id: string
  created_at: string
  updated_at: string
  name: string
  s3_uri: string
  content_type: string
  kind: "marketing" | "freight" | "purchasing"
  source: string
  integration_id: string | null
  summary: string | null
  analysis_status: "pending" | "analyzing" | "completed" | "failed"
  analysis_error: string | null
  version: number
  previous_version_id: string | null
  is_active: boolean
}

export interface UploadContractMeta {
  kind: "marketing" | "freight" | "purchasing"
  source: string
  integration_id?: string
}

// API Functions

/** Contracts for one connection when `integration_id` is given (its name is not always a source,
 * e.g. `unfi-canada` files as `unfi`), otherwise for a source. */
export async function listContracts(source?: string, integration_id?: string): Promise<ContractDocument[]> {
  const params = integration_id ? { integration_id } : source ? { source } : undefined
  return api_fetch_throws<ContractDocument[]>("/contracts", { params })
}

export async function uploadContract(file: File, meta: UploadContractMeta): Promise<ContractDocument> {
  const formData = new FormData()
  formData.append("file", file)
  formData.append("upload_meta", JSON.stringify(meta))

  const response = await fetch("/api/contracts", {
    method: "POST",
    credentials: "same-origin",
    body: formData,
  })

  if (!response.ok) {
    const text = await response.text()
    let message
    try {
      const data = JSON.parse(text)
      message = data.message
    } catch {
      message = text
    }
    throw new Error(message || "Failed to upload contract")
  }

  return response.json()
}

export async function getContract(id: string): Promise<ContractDocument> {
  return api_fetch_throws<ContractDocument>(`/contracts/${id}`)
}

export async function updateContract(id: string, data: { summary?: string }): Promise<ContractDocument> {
  return api_fetch_throws<ContractDocument>(`/contracts/${id}`, {
    method: "PUT",
    body: data,
  })
}

export async function deleteContract(id: string): Promise<void> {
  const res = await api_fetch<void>(`/contracts/${id}`, { method: "DELETE" })
  if (!res.ok) {
    throw res.error
  }
}

export async function analyzeContract(id: string): Promise<ContractDocument> {
  return api_fetch_throws<ContractDocument>(`/contracts/${id}/analyze`, {
    method: "POST",
    body: {},
  })
}

export async function uploadNewVersion(id: string, file: File): Promise<ContractDocument> {
  const formData = new FormData()
  formData.append("file", file)

  const response = await fetch(`/api/contracts/${id}/upload-version`, {
    method: "POST",
    credentials: "same-origin",
    body: formData,
  })

  if (!response.ok) {
    const text = await response.text()
    let message
    try {
      const data = JSON.parse(text)
      message = data.message
    } catch {
      message = text
    }
    throw new Error(message || "Failed to upload new version")
  }

  return response.json()
}
